import type { VenueBook } from '@/views/ValueDashboard/types';

/** Ticker-scalar authority (kalshi-line-availability-book-proof, A2 /
 *  DNL-R6-P1). A Kalshi book's `lastPriceCents` and `volume` are
 *  ticker/trade-channel values proven only by a market frame's own evidence.
 *  While `tickerSourceValidity === 'unconfirmed'` the book still carries the
 *  last figure the store holds (`volume` is a required numeric field), but that
 *  figure is NOT a confirmed observation this session: every reader that
 *  displays it, sums it or ranks on it must treat it as absent. This is the one
 *  predicate those readers share. Books without the field (legacy feeds,
 *  Polymarket, synthetic) are observed. */
export function tickerScalarsObserved(book: VenueBook | null | undefined): boolean {
  return book?.tickerSourceValidity !== 'unconfirmed';
}

/** The venue-reported `volume` when it is an observed figure, else `null`
 *  (no book, or ticker authority unconfirmed). Depth-derived provenance
 *  (`volumeIsDepthDerived`) stays the caller's concern — it is a different
 *  truthfulness cue, not an authority. */
export function observedVolume(book: VenueBook | null | undefined): number | null {
  return book != null && tickerScalarsObserved(book) ? book.volume : null;
}

/** An aggregate of observed volumes that PRESERVES absence (DNL-R6-P1,
 *  round 7): `missing` when no listed book has an observed figure, `partial`
 *  when some do (the value is the observed sum only and must never present
 *  itself as complete), `complete` when all do. A measured zero is a value —
 *  `{ value: 0, coverage: 'complete' }` — never conflated with missing. Absent
 *  venues (null/undefined entries) are not missing observations. Same shape
 *  as the discovery `DiscoveryVolume`, ticker authority only. */
export interface ObservedVolumeSum {
  value: number | null;
  coverage: 'complete' | 'partial' | 'missing';
}
export function observedVolumeSum(books: readonly (VenueBook | null | undefined)[]): ObservedVolumeSum {
  const listed = books.filter((b): b is VenueBook => b != null);
  const known = listed.map(observedVolume).filter((v): v is number => v !== null && Number.isFinite(v));
  if (listed.length === 0 || known.length === 0) return { value: null, coverage: 'missing' };
  return { value: known.reduce((s, v) => s + v, 0), coverage: known.length === listed.length ? 'complete' : 'partial' };
}

/** Render text for an {@link ObservedVolumeSum}: dash when missing, the
 *  figure with the "~" partial cue the dashboard's Vol cells use otherwise. */
export function formatObservedVolumeSum(sum: ObservedVolumeSum): string {
  if (sum.value === null) return '—';
  return `${sum.coverage === 'partial' ? '~' : ''}${sum.value.toLocaleString()}`;
}
export const OBSERVED_VOLUME_MISSING_TITLE =
  'Not yet observed — no ticker quote for this market since the feed’s current generation began';
export const OBSERVED_VOLUME_PARTIAL_TITLE =
  'Only the observed markets are counted — another market in this event has had no ticker quote since the feed’s current generation began, so this is a partial figure';
export function observedVolumeSumTitle(sum: ObservedVolumeSum): string | undefined {
  return sum.coverage === 'missing' ? OBSERVED_VOLUME_MISSING_TITLE
    : sum.coverage === 'partial' ? OBSERVED_VOLUME_PARTIAL_TITLE : undefined;
}
